import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import emailMask from 'text-mask-addons/dist/emailMask';
import NotificationSystem from 'react-notification-system';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import Loading from 'react-loading-components';

import Header from 'components/Header';
import Input from 'components/Form/Input';
import FormGroupLabel from 'components/FormGroupLabel';
import { Button } from 'components/Button';
import Popup from 'components/Popup';
import Select from 'components/Form/Select';
import Validator from 'components/Validator/Validator';
import { Grid, Column } from 'components/Layout';

import {
  checkinAction,
} from 'actions/workflow';

import {
  getApplications,
} from 'actions/application';

import { toTitleCase } from 'utils/toTitleCase';
import { formatCurrency } from 'utils/formatCurrency';
import { numberUnmask, currencyMask, tdaCurrencyMask, unmask } from 'utils/masks';
import states from 'utils/statesForPreSelection';
import unSupportedStates from 'utils/unSupportedStates';
import schema from './schema';
import style from './style.scss';

const defaultValues = {
  bankruptcy: undefined,
  financialHardship: undefined,
  currentWithCarPayment: undefined,
  currentWithMortgage: undefined,
  rentOrOwn: undefined,
};

const doneTypingInterval = 1000;
let typingTimer;

const PreSelectionStart = (props) => {
  const { validator: { values, errors } } = props;
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingApplication, setIsLoadingApplication] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [monthlySavingAmounts, setMonthlySavingAmounts] = useState(null);
  const [term, setTerm] = useState(null);
  const [ficoScore, setFicoScore] = useState('');
  const [termAndFicoErrors, setTermAndFicoErrors] = useState(null);
  const [isAddressPopupVisible, setIsAddressPopupVisible] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const notification = useRef(null);

  useEffect(() => {
    const { validator: { setValues, resetErrors } } = props;
    setValues({ ...defaultValues });
    resetErrors();
  }, []);

  const handleRefreshClientId = () => {
    setIsLoadingApplication(true);
    const { validator: { setValues, resetErrors } } = props;
    const clientId = get(values, 'clientId', '');
    const merchantId = localStorage.getItem('merchantId') ? localStorage.getItem('merchantId') : '';

    const url = `/applications-view/search?channelAttributesClientId=${clientId}&merchantId=${merchantId}&limit=${10}&channelCode=fsl`;
    dispatch(getApplications({
      url,
      success: (res) => {
        setIsLoadingApplication(false);
        if (get(res, 'data', []).length > 1) {
          notification.current
          && notification.current.addNotification({
            message: 'Exists multiple applications with these client id and merchant.',
            level: 'error',
            position: 'tc',
          });
          return;
        }
        if (!get(res, 'data', []).length) {
          return;
        }

        const statusCode = get(res, 'data.0.statusCode');
        if (statusCode === '5038') {
          notification.current
          && notification.current.addNotification({
            message: 'Client is already Pre-selected',
            level: 'error',
            position: 'tc',
          });
          return;
        }

        if (statusCode !== '5039') {
          notification.current
          && notification.current.addNotification({
            message: `Pre-selection Tool unavailable for ${get(res, 'data.0.statusLabel')} status`,
            level: 'error',
            position: 'tc',
          });
          return;
        }

        const applicationId = get(res, 'data.0.applicationId');

        setIsLoadingApplication(true);
        resetErrors();
        dispatch(getApplications({
          url: `/fsl-settlements/${applicationId}/pre-selection`,
          success: (appData) => {
            setIsLoadingApplication(false);
            setValues({
              requestedAmount: `$ ${formatCurrency(get(appData, 'input.requestedAmount'), 2)}`,
              grossMonthlyIncome: `$ ${formatCurrency(get(appData, 'input.grossMonthlyIncome'), 2)}`,
              monthlyHousingPayment: `$ ${formatCurrency(get(appData, 'input.monthlyHousingPayment'), 2)}`,
              monthlyCarPayment: `$ ${formatCurrency(get(appData, 'input.monthlyCarPayment'), 2)}`,
              clientId: get(appData, 'clientId'),
              firstName: get(appData, 'input.firstName'),
              lastName: get(appData, 'input.lastName'),
              streetAddress: get(appData, 'input.streetAddress'),
              city: get(appData, 'input.city'),
              state: get(appData, 'input.state'),
              zip: get(appData, 'input.zip'),
              phone: get(appData, 'input.phone'),
              email: get(appData, 'input.email'),
              bankruptcy: get(appData, 'input.bankruptcy'),
              financialHardship: get(appData, 'input.financialHardship'),
              rentOrOwn: get(appData, 'input.rentOrOwn'),
              currentWithCarPayment: get(appData, 'input.currentWithCarPayment'),
              currentWithMortgage: get(appData, 'input.currentWithMortgage'),
              carPayment: get(appData, 'input.carPayment'),
            });
            notification.current
            && notification.current.addNotification({
              message: 'Successfully loaded.',
              level: 'success',
              position: 'tc',
            });
          },
          fail: (err) => {
            setIsLoadingApplication(false);
            notification.current
            && notification.current.addNotification({
              message: err.message || 'Failed loading Application',
              level: 'error',
              position: 'tc',
            });
          },
        }));
      },
      fail: (err) => {
        setIsLoadingApplication(false);

        notification.current
        && notification.current.addNotification({
          message: get(err, 'data.message', 'An unexpected error has occurred, please try again'),
          level: 'error',
          position: 'tc',
        });
      },
    }));
  };

  const onKeyUpHandler = () => {
    typingTimer && clearTimeout(typingTimer);
    typingTimer = setTimeout(handleRefreshClientId, doneTypingInterval);
  };

  const onKeyDownHandler = () => {
    typingTimer && clearTimeout(typingTimer);
  };

  const handleButtonClick = (e) => {
    e.preventDefault();

    const { validator: { validate }, history } = props;

    const formData = {
      ...values,
      clientId: (get(values, 'clientId') || '').trim().replace(/\s\s+/g, ' '),
      requestedAmount: unmask(get(values, 'requestedAmount') || ''),
      grossMonthlyIncome: unmask(get(values, 'grossMonthlyIncome') || ''),
      monthlyHousingPayment: unmask(get(values, 'monthlyHousingPayment') || ''),
      monthlyCarPayment: unmask(get(values, 'monthlyCarPayment') || ''),
      phone: numberUnmask(get(values, 'phone') || ''),
      merchantId: localStorage.getItem('merchantId'),
    };
    ['bankruptcy', 'financialHardship', 'carPayment', 'currentWithCarPayment', 'currentWithMortgage'].forEach((key) => {
      if (includes([true, false, 'true', 'false'], get(values, key))) {
        formData[key] = (get(values, key) === true || get(values, key) === 'true');
      } else {
        delete formData[key];
      }
    });

    if (!numberUnmask(get(values, 'monthlyCarPayment', ''))) {
      delete formData.monthlyCarPayment;
    }

    if (currentStep === 1) {
      if (validate(schema).isValid) {
        setIsLoading(true);
        dispatch(checkinAction({
          data: formData,
          url: '/fsl-settlements/pre-selection/calculate',
          success: (response) => {
            setIsLoading(false);
            setCurrentStep(2);
            setMonthlySavingAmounts(get(response, 'terms'));
          },
          fail: (error) => {
            setIsLoading(false);
            notification.current
            && notification.current.addNotification({
              message: get(
                error,
                'data.message',
                'We have encountered an error, please try again.'
              ),
              level: 'error',
              position: 'tc',
            });
          },
        }));
      }
    } else {
      let isValid = true;
      if (!term) {
        isValid = false;
        setTermAndFicoErrors({
          terms: 'One of terms should be selected.',
        });
      }
      if (!ficoScore) {
        isValid = false;
        setTermAndFicoErrors(Object.assign(
          {},
          termAndFicoErrors,
          { fico: 'Fico field is required' }
        ));
      }
      if (!isValid) return;

      setIsLoading(true);
      dispatch(checkinAction({
        data: {
          PreSelection: Object.assign(
            {},
            formData,
            {
              ficoScore,
              term: term === 'null' ? null : parseInt(term, 10),
            }
          ),
        },
        url: '/workflows/application/workflow/fsl1/start',
        success: (res) => {
          history.push(get(res, 'state.url', ''));
        },
        fail: (error) => {
          setIsLoading(false);
          notification.current
          && notification.current.addNotification({
            message: get(
              error,
              'data.message',
              'We have encountered an error, please try again.'
            ),
            level: 'error',
            position: 'tc',
          });
        },
      }));
    }
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler, setValues } } = props;
    switch (event.target.name) {
      case 'firstName':
      case 'lastName':
        onChangeHandler(event.target.name, (event.target.value).replace(/[^a-zA-Z-' ]/g, ''));
        break;
      case 'term':
        setTerm(event.target.id);
        break;
      case 'ficoScore':
        setFicoScore(event.target.value);
        break;
      case 'carPayment':
        if (event.target.value === 'false') {
          setValues({
            ...values,
            carPayment: 'false',
            monthlyCarPayment: undefined,
            currentWithCarPayment: undefined,
          });
        } else if (event.target.value === 'true') {
          setValues({
            ...values,
            carPayment: 'true',
            monthlyCarPayment: undefined,
            currentWithCarPayment: undefined,
          });
        } else {
          setValues({
            ...values,
            carPayment: undefined,
            monthlyCarPayment: undefined,
            currentWithCarPayment: undefined,
          });
        }
        break;
      case 'rentOrOwn':
        if (event.target.value === 'Own') {
          setValues({
            ...values,
            rentOrOwn: 'Own',
            currentWithMortgage: undefined,
          });
        } else if (event.target.value === 'Rent') {
          setValues({
            ...values,
            rentOrOwn: 'Rent',
            currentWithMortgage: undefined,
          });
        } else {
          setValues({
            ...values,
            rentOrOwn: undefined,
            currentWithMortgage: undefined,
          });
        }
        break;
      case 'state':
        if (unSupportedStates.indexOf(event.target.value) !== -1) {
          setIsAddressPopupVisible(true);
          setPopupType(4);
        }
        onChangeHandler(event.target.name, event.target.value);
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
        break;
    }
  };

  const handleBlur = (event) => {
    event.preventDefault();

    const { validator: { onChangeHandler } } = props;
    if (event.target.name === 'clientId') {
      handleRefreshClientId();
    } else {
      onChangeHandler(event.target.name, toTitleCase(event.target.value));
    }
  };

  const handleAbort = () => {
    setIsAddressPopupVisible(false);
  };

  const handleGoBack = () => {
    setCurrentStep(1);
  };

  return (
    <Fragment>
      <Header />
      <NotificationSystem ref={notification} />
      <section className={cn('container', 'section', style['pre-selection-start'])}>
        <div className="grid-container fluid borrowers-apply">
          <div className="grid-container">
            <Grid className="max-limited justify-content-center">
              <Column className="small-12 large-10 large-offset-1">
                <div className="form-group">
                  {currentStep === 1 && (
                    <Grid className="grid-margin-x">
                      <Column className="small-12 medium-12">
                        <FormGroupLabel value="Pre-Selection Tool (Step 1)" />
                      </Column>
                      <Column className="small-12 medium-6">
                        <Input
                          label="Client ID"
                          name="clientId"
                          labelClassName={style['bg-white']}
                          value={values.clientId}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          hasError={!!errors.clientId}
                          isRequired
                          errorMessage={errors.clientId}
                          onKeyUp={onKeyUpHandler}
                          onKeyDown={onKeyDownHandler}
                        />
                      </Column>
                      <Column className={cn('small-12 medium-6', 'loading-icon-container', style['clientid-loading-icon-container'])}>
                        { isLoadingApplication && <Loading type="oval" width={30} height={30} fill="#3adb76" /> }
                      </Column>
                      <Column className="small-12 medium-6">
                        <Input
                          label="Requested Amount"
                          name="requestedAmount"
                          labelClassName={style['bg-white']}
                          value={values.requestedAmount}
                          onChange={handleInputChange}
                          isMasked={tdaCurrencyMask}
                          hasError={!!errors.requestedAmount}
                          placeHolder="($1,000.00 - $50,000.00)"
                          isRequired
                          errorMessage={errors.requestedAmount}
                        />
                      </Column>
                      <Column className="small-12 medium-6">
                        <div />
                      </Column>
                      <Column className="small-12 medium-6">
                        <Input
                          label="First Name"
                          name="firstName"
                          labelClassName={style['bg-white']}
                          value={values.firstName}
                          onChange={handleInputChange}
                          hasError={!!errors.firstName}
                          isRequired
                          onBlur={handleBlur}
                          errorMessage={errors.firstName}
                        />
                      </Column>
                      <Column className="small-12 medium-6">
                        <Input
                          label="Last Name"
                          name="lastName"
                          labelClassName={style['bg-white']}
                          value={values.lastName}
                          onChange={handleInputChange}
                          hasError={!!errors.lastName}
                          isRequired
                          onBlur={handleBlur}
                          errorMessage={errors.lastName}
                        />
                      </Column>
                      <Column className="small-12 medium-12 position-relative">
                        <Input
                          label="Street Address"
                          name="streetAddress"
                          labelClassName={style['bg-white']}
                          value={values.streetAddress}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          hasError={!!errors.streetAddress}
                          isRequired
                          errorMessage={errors.streetAddress}
                        />
                        { isAddressPopupVisible &&
                          <Popup
                            handleAbort={handleAbort}
                            type={popupType}
                            data={{
                              address: get(values, 'address'),
                              city: get(values, 'city'),
                              state: get(values, 'state'),
                              zipcode: get(values, 'zip'),
                            }}
                          />
                        }
                      </Column>
                      <Column className="small-12 medium-4">
                        <Input
                          label="City"
                          name="city"
                          value={values.city}
                          labelClassName={style['bg-white']}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          hasError={!!errors.city}
                          isRequired
                          errorMessage={errors.city}
                        />
                      </Column>
                      <Column className="small-12 medium-4">
                        <Select
                          name="state"
                          data={states}
                          value={values.state}
                          onChange={handleInputChange}
                          label="State"
                          isRequired
                          hasError={
                            !!errors.state
                              || (unSupportedStates.indexOf(get(values, 'state')) !== -1)
                          }
                          errorMessage={errors.state}
                        />
                      </Column>
                      <Column className="small-12 medium-4">
                        <Input
                          label="Zip"
                          name="zip"
                          labelClassName={style['bg-white']}
                          value={values.zip}
                          onChange={handleInputChange}
                          hasError={!!errors.zip}
                          isRequired
                          errorMessage={errors.zip}
                        />
                      </Column>
                      <Column className="small-12 medium-5">
                        <Input
                          label="Phone Number"
                          name="phone"
                          labelClassName={style['bg-white']}
                          placeHolder="(___) ___-____"
                          isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                          value={values.phone}
                          onChange={handleInputChange}
                          hasError={!!errors.phone}
                          isRequired
                          errorMessage={errors.phone}
                        />
                      </Column>
                      <Column className="small-12 medium-7">
                        <Input
                          label="Email Address"
                          name="email"
                          labelClassName={style['bg-white']}
                          isMasked={emailMask}
                          value={values.email}
                          onChange={handleInputChange}
                          hasError={!!errors.email}
                          isRequired
                          errorMessage={errors.email}
                        />
                      </Column>
                      <Column className="small-12 medium-7 pt-dot5">
                        Gross Monthly Income
                      </Column>
                      <Column className="small-12 medium-5">
                        <Input
                          label="Gross Monthly Income"
                          name="grossMonthlyIncome"
                          labelClassName={style['bg-white']}
                          isMasked={currencyMask}
                          value={values.grossMonthlyIncome}
                          onChange={handleInputChange}
                          hasError={!!errors.grossMonthlyIncome}
                          isRequired
                          errorMessage={errors.grossMonthlyIncome}
                        />
                      </Column>
                      <Column className="small-12 medium-7 pt-dot5">
                        Do you currently have an open bankruptcy case?
                      </Column>
                      <Column className="small-12 medium-5">
                        <Select
                          name="bankruptcy"
                          value={values.bankruptcy}
                          onChange={handleInputChange}
                          data={[
                            { value: true, title: 'Yes' },
                            { value: false, title: 'No' },
                          ]}
                          hasError={!!errors.bankruptcy}
                          errorMessage={errors.bankruptcy}
                          isRequired
                        />
                      </Column>
                      <Column className="small-12 medium-7 pt-dot5">
                        Are you currently experiencing a financial hardship?
                      </Column>
                      <Column className="small-12 medium-5">
                        <Select
                          name="financialHardship"
                          value={values.financialHardship}
                          onChange={handleInputChange}
                          data={[
                            { value: true, title: 'Yes' },
                            { value: false, title: 'No' },
                          ]}
                          hasError={!!errors.financialHardship}
                          errorMessage={errors.financialHardship}
                          isRequired
                        />
                      </Column>
                      <Column className="small-12 medium-7 pt-dot5">
                        Do you rent or own your home?
                      </Column>
                      <Column className="small-12 medium-5">
                        <Select
                          name="rentOrOwn"
                          value={values.rentOrOwn}
                          onChange={handleInputChange}
                          data={[
                            { value: 'Rent', title: 'Rent' },
                            { value: 'Own', title: 'Own' },
                          ]}
                          hasError={!!errors.rentOrOwn}
                          errorMessage={errors.rentOrOwn}
                          isRequired
                        />
                      </Column>
                      <Column className="small-12 medium-6 medium-offset-1 pt-dot5">
                        What is your monthly housing payment?
                      </Column>
                      <Column className="small-12 medium-5">
                        <Input
                          label="Monthly Housing Payment"
                          name="monthlyHousingPayment"
                          labelClassName={style['bg-white']}
                          isMasked={currencyMask}
                          value={values.monthlyHousingPayment}
                          onChange={handleInputChange}
                          hasError={!!errors.monthlyHousingPayment}
                          isRequired
                          errorMessage={errors.monthlyHousingPayment}
                        />
                      </Column>
                      {
                        values.rentOrOwn === 'Own' && (
                          <Fragment>
                            <Column className="small-12 medium-6 medium-offset-1 pt-dot5">
                              Are you current with your mortgage?
                            </Column>
                            <Column className="small-12 medium-5">
                              <Select
                                name="currentWithMortgage"
                                value={values.currentWithMortgage}
                                onChange={handleInputChange}
                                data={[
                                  { value: true, title: 'Yes' },
                                  { value: false, title: 'No' },
                                ]}
                                hasError={!!errors.currentWithMortgage}
                                errorMessage={errors.currentWithMortgage}
                                isRequired
                              />
                            </Column>
                          </Fragment>
                        )
                      }
                      <Column className="small-12 medium-7 pt-dot5">
                        Do you have a car payment?
                      </Column>
                      <Column className="small-12 medium-5">
                        <Select
                          name="carPayment"
                          value={values.carPayment}
                          onChange={handleInputChange}
                          data={[
                            { value: true, title: 'Yes' },
                            { value: false, title: 'No' },
                          ]}
                          hasError={!!errors.carPayment}
                          errorMessage={errors.carPayment}
                          isRequired
                        />
                      </Column>
                      {(values.carPayment === 'true' || values.carPayment === true) && (
                        <Fragment>
                          <Column className="small-12 medium-6 medium-offset-1 pt-dot5">
                            What is your monthly car payment?
                          </Column>
                          <Column className="small-12 medium-5">
                            <Input
                              label="Monthly Car Payment"
                              name="monthlyCarPayment"
                              labelClassName={style['bg-white']}
                              isMasked={currencyMask}
                              value={values.monthlyCarPayment}
                              onChange={handleInputChange}
                              hasError={!!errors.monthlyCarPayment}
                              isRequired
                              errorMessage={errors.monthlyCarPayment}
                            />
                          </Column>
                          <Column className="small-12 medium-6 medium-offset-1 pt-dot5">
                            Are you current with your car payment?
                          </Column>
                          <Column className="small-12 medium-5">
                            <Select
                              name="currentWithCarPayment"
                              value={values.currentWithCarPayment}
                              onChange={handleInputChange}
                              data={[
                                { value: true, title: 'Yes' },
                                { value: false, title: 'No' },
                              ]}
                              hasError={!!errors.currentWithCarPayment}
                              errorMessage={errors.currentWithCarPayment}
                              isRequired
                            />
                          </Column>
                        </Fragment>
                      )}
                      <Column className="small-12 medium-4 medium-offset-2">
                        <Button
                          className={cn('button large green w-100', style.button)}
                          onClick={handleButtonClick}
                          isLoading={isLoading}
                          isDisabled={
                            isLoadingApplication
                              || isLoading
                              || (unSupportedStates.indexOf(get(values, 'state')) !== -1)
                          }
                        >
                          Next
                        </Button>
                      </Column>
                    </Grid>
                  )}
                  {currentStep === 2 && (
                    <Fragment>
                      <Column className="small-12">
                        <FormGroupLabel value="Pre-Selection Tool (Step 2)" />
                      </Column>
                      <p>
                        How much can you commit to save in your Customer Dedicated Account each month for the next 6 months?
                      </p>
                      <Column className="small-12">
                        <input
                          type="radio"
                          name="term"
                          id="24"
                          onClick={handleInputChange}
                        />
                        <label htmlFor="24">
                          ${
                            formatCurrency(get(find(monthlySavingAmounts, { term: 24 }), 'monthlySavings', ''), 2)
                          } (to be considered for a 24-month loan)
                        </label>
                      </Column>
                      <Column className="small-12">
                        <input
                          type="radio"
                          name="term"
                          id="36"
                          onClick={handleInputChange}
                        />
                        <label htmlFor="36">
                          ${
                            formatCurrency(get(find(monthlySavingAmounts, { term: 36 }), 'monthlySavings', ''), 2)
                          } (to be considered for a 36-month loan)
                        </label>
                      </Column>
                      <Column className="small-12">
                        <input
                          type="radio"
                          name="term"
                          id="48"
                          onClick={handleInputChange}
                        />
                        <label htmlFor="48">
                          ${
                            formatCurrency(get(find(monthlySavingAmounts, { term: 48 }), 'monthlySavings', ''), 2)
                          } (to be considered for a 48-month loan)
                        </label>
                      </Column>
                      <Column className="small-12">
                        <input
                          type="radio"
                          name="term"
                          id="60"
                          onClick={handleInputChange}
                        />
                        <label htmlFor="60">
                          ${
                            formatCurrency(get(find(monthlySavingAmounts, { term: 60 }), 'monthlySavings', ''), 2)
                          } (to be considered for a 60-month loan)
                        </label>
                      </Column>
                      <Column className="small-12">
                        <input
                          type="radio"
                          name="term"
                          id="null"
                          onClick={handleInputChange}
                        />
                        <label htmlFor="null">Cannot save any of these amounts</label>
                      </Column>
                      <Grid className="mt-1">
                        <Column className="small-6 medium-4">FICO range</Column>
                        <Column className="small-6 medium-4">
                          <Select
                            name="ficoScore"
                            label="FICO range"
                            value={ficoScore}
                            onChange={handleInputChange}
                            data={[
                              { value: '700 - 850', title: '700 - 850' },
                              { value: '600 - 699', title: '600 - 699' },
                              { value: '500 - 599', title: '500 - 599' },
                              { value: 'Below 500', title: 'Below 500' },
                            ]}
                            isRequired
                          />
                        </Column>
                      </Grid>
                      <Column className="small-12 text-center mt-2">
                        <Button
                          className="button green mr-1"
                          onClick={handleGoBack}
                          isDisabled={
                            isLoadingApplication
                              || isLoading
                          }
                        >
                          Back
                        </Button>
                        <Button
                          className="button green"
                          onClick={handleButtonClick}
                          isLoading={isLoading}
                          isDisabled={
                            isLoadingApplication
                              || isLoading
                          }
                        >
                          Submit
                        </Button>
                      </Column>
                      <Column className="small-12 text-center text-danger">
                        {
                          termAndFicoErrors && Object.keys(termAndFicoErrors).map(key => (<p>{termAndFicoErrors[key]}</p>))
                        }
                      </Column>
                      <p className="mt-2">
                        <small>
                          Please note that our estimated loan amount is based on 100% settlement of the total debt amount which includes 75% settlement rate and 25% performance fee.
                          <br />
                          <br />
                          Disclaimer: This is just an estimation tool to help guide you on the monthly payment savings amount a borrower would need to make over 6 months to be considered for an FSL loan, other pre-selection criteria also apply. All pre-selections are subject to change upon application submission.
                          <br />
                          <br />
                          Proprietary & Confidential
                        </small>
                      </p>
                    </Fragment>
                  )}
                </div>
              </Column>
            </Grid>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

PreSelectionStart.propTypes = {
  validator: PropTypes.object.isRequired,
  history: PropTypes.func.isRequired,
};

PreSelectionStart.defaultProps = {

};

export default Validator(schema, defaultValues)(PreSelectionStart);



// WEBPACK FOOTER //
// ./src/containers/pages/PreSelection/Start/index.js